import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useParams, matchPath } from "react-router-dom";
import { MessageCircle, X, Check, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Floating footer feedback button. Signed-in users pick "Site" or "Blog" feedback
 * (Blog is only offered if we're currently on /blogs/:slug — the slug + title get
 * attached automatically). Submits to POST /api/feedback and shows up in the
 * Admin > Feedback tab.
 */
export default function FeedbackButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("choose"); // choose | write | done
  const [ftype, setFtype] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [blogInfo, setBlogInfo] = useState(null);

  // Detect if the user is currently on a blog page and pull its slug + title
  const blogMatch = matchPath({ path: "/blogs/:slug", end: false }, location.pathname);
  const currentSlug = blogMatch?.params?.slug || null;

  useEffect(() => {
    if (!open) {
      // reset when reopening
      setStep("choose"); setFtype(null); setBody(""); setBlogInfo(null); return;
    }
    if (!currentSlug) return;
    api.get(`/blogs/${currentSlug}`).then(r => {
      setBlogInfo({ slug: r.data.slug, title: r.data.title });
    }).catch(() => setBlogInfo(null));
  }, [open, currentSlug]);

  const startWrite = (t) => {
    if (t === "blog" && !blogInfo) {
      toast.info("Open a blog first to leave blog-specific feedback.");
      return;
    }
    setFtype(t); setStep("write");
  };

  const submit = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post("/feedback", {
        type: ftype,
        body: body.trim(),
        blog_slug: ftype === "blog" ? blogInfo?.slug : undefined,
      });
      setStep("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send feedback");
    } finally { setSending(false); }
  };

  return (
    <>
      <button data-testid="footer-feedback-btn"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-[0.2em] rounded-full border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-sky-600 hover:text-sky-700 dark:hover:text-sky-400 transition-colors">
        <MessageCircle className="w-3.5 h-3.5"/> Feedback
      </button>

      {open && createPortal(
        <div data-testid="feedback-modal"
             className="fixed inset-0 z-[9999] bg-black/50 grid place-items-center p-4"
             style={{ pointerEvents: "auto" }}
             onClick={() => setOpen(false)}>
          <div onClick={e => e.stopPropagation()}
               className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg w-full max-w-md shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2">
                {step === "write" && (
                  <button onClick={() => setStep("choose")} data-testid="feedback-back"
                          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                    <ArrowLeft className="w-4 h-4"/>
                  </button>
                )}
                <div className="font-heading font-semibold text-slate-900 dark:text-slate-100">
                  {step === "done" ? "Thank you" : "Send feedback"}
                </div>
              </div>
              <button data-testid="feedback-close" onClick={() => setOpen(false)}
                      className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                <X className="w-4 h-4"/>
              </button>
            </div>

            <div className="p-4">
              {!user && (
                <div data-testid="feedback-signin-prompt" className="text-sm text-slate-600 dark:text-slate-300 border border-dashed border-slate-300 dark:border-slate-700 rounded-md p-4 text-center">
                  Please sign in to leave feedback.
                </div>
              )}

              {user && step === "choose" && (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">What&rsquo;s this about?</p>
                  <button data-testid="feedback-choose-site"
                          onClick={() => startWrite("site")}
                          className="w-full text-left border border-slate-200 dark:border-slate-700 rounded-md p-3 hover:border-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950 transition-colors">
                    <div className="font-medium text-slate-900 dark:text-slate-100">Site feedback</div>
                    <div className="text-xs text-slate-500 mt-0.5">General suggestions or issues about Hospice Synopsis.</div>
                  </button>
                  <button data-testid="feedback-choose-blog"
                          disabled={!blogInfo}
                          onClick={() => startWrite("blog")}
                          className={`w-full text-left border rounded-md p-3 transition-colors ${
                            blogInfo
                              ? "border-slate-200 dark:border-slate-700 hover:border-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950"
                              : "border-slate-100 dark:border-slate-800 opacity-60 cursor-not-allowed"}`}>
                    <div className="font-medium text-slate-900 dark:text-slate-100">Blog feedback</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {blogInfo ? <>About the blog <span className="text-sky-700">&ldquo;{blogInfo.title}&rdquo;</span></>
                                : "Open a blog first to leave blog-specific feedback."}
                    </div>
                  </button>
                </div>
              )}

              {user && step === "write" && (
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                    {ftype === "blog" ? <>Blog · <span className="text-sky-700">{blogInfo?.title}</span></> : "Site feedback"}
                  </div>
                  <textarea data-testid="feedback-body"
                            rows={5} value={body} onChange={e => setBody(e.target.value)}
                            placeholder="Tell us what could be better…"
                            maxLength={4000}
                            className="w-full text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded p-2 outline-none focus:border-sky-600"/>
                  <div className="mt-1 text-[10px] text-slate-400 text-right">{body.length}/4000</div>
                  <div className="mt-3 flex justify-end gap-2">
                    <button onClick={() => setOpen(false)}
                            className="text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900 px-3 py-1.5">Cancel</button>
                    <button data-testid="feedback-submit"
                            disabled={!body.trim() || sending}
                            onClick={submit}
                            className="text-xs uppercase tracking-widest bg-sky-700 hover:bg-sky-600 disabled:opacity-60 text-white px-3 py-1.5 rounded">
                      {sending ? "Sending…" : "Send"}
                    </button>
                  </div>
                </div>
              )}

              {step === "done" && (
                <div data-testid="feedback-done" className="text-center py-4">
                  <div className="w-10 h-10 mx-auto rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mb-2">
                    <Check className="w-5 h-5"/>
                  </div>
                  <div className="font-medium text-slate-900 dark:text-slate-100">Feedback sent</div>
                  <p className="text-sm text-slate-500 mt-1">Thanks — we&rsquo;ll review it and get back to you if needed.</p>
                  <button onClick={() => setOpen(false)}
                          className="mt-4 text-xs uppercase tracking-widest bg-slate-900 hover:bg-slate-700 text-white px-4 py-2 rounded">
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
